import React, { useState, useRef, useEffect } from "react";
import Card from "components/card";
import BarChart from "components/charts/BarChart";
import {
  barChartDataWeeklyRevenue,
  barChartOptionsWeeklyRevenue,
} from "variables/charts";
import { MdRefresh } from "react-icons/md";
import { getToken } from "utils/auth";



const WeeklyRevenue = ({ loading, chartData, onDataRefreshed }) => {
  const [localChartData, setLocalChartData] = useState(chartData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Handle refresh button click
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3002";
      const token = getToken();
      const response = await fetch(`${baseUrl}/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh dashboard data");
      }

      const result = await response.json();
      const freshData = result.data;

      // Update local state with fresh data
      setLocalChartData(freshData?.leadClientStatusChart);

      // Call parent callback if provided
      if (onDataRefreshed) {
        onDataRefreshed(freshData);
      }

      // Show success toast
      if (window.__showToast) {
        window.__showToast("Lead & Client Status refreshed successfully", "success");
      }
    } catch (err) {
      setRefreshError(err.message);
      // Show error toast
      if (window.__showToast) {
        window.__showToast(
          `Failed to refresh data: ${err.message}`,
          "error"
        );
      }
    } finally {
      setIsRefreshing(false);
      // Clear error after 5 seconds
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshError(null);
      }, 5000);
    }
  };

  // Sync local state with props when they change
  useEffect(() => {
    if (!isRefreshing) {
      setLocalChartData(chartData);
    }
  }, [chartData, isRefreshing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Use API data as-is if present and valid
  let categories = barChartOptionsWeeklyRevenue.xaxis.categories;
  let chartSeries = barChartDataWeeklyRevenue;
  if (
    localChartData &&
    Array.isArray(localChartData.categories) && localChartData.categories.length > 0 &&
    Array.isArray(localChartData.chartData) && localChartData.chartData.length > 0 &&
    localChartData.chartData[0].data
  ) {
    categories = localChartData.categories;
    chartSeries = localChartData.chartData;
  }
  const chartOptions = {
    ...barChartOptionsWeeklyRevenue,
    xaxis: { ...barChartOptionsWeeklyRevenue.xaxis, categories },
  };

  return (
    <>
      <style>{`
        @keyframes refresh-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .refresh-icon-spin {
          animation: refresh-spin 1s linear infinite;
        }
      `}</style>
      <Card extra="flex flex-col bg-white w-full rounded-3xl py-6 px-2 text-center">
        <div className="mb-auto flex items-center justify-between px-6">
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">
            Lead & Client Status
          </h2>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 ${isRefreshing
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 hover:cursor-pointer"
              }`}
            title={isRefreshing ? "Refreshing data..." : "Refresh Lead & Client Status"}
          >
            <MdRefresh
              className={`h-6 w-6 ${isRefreshing ? "refresh-icon-spin" : ""}`}
            />
          </button>
        </div>

        <div className="md:mt-16 lg:mt-0">
          <div className="h-[250px] w-full xl:h-[350px]">
            <BarChart
              chartData={chartSeries}
              chartOptions={chartOptions}
            />
          </div>
        </div>
      </Card>
    </>
  );
};

export default WeeklyRevenue;
